import type { WorkspaceEvaluationPlanLevelSettings } from '../types/evaluationPlan';
import { emptyWorkspaceEvaluationPlanLevelSettings } from '../types/evaluationPlan';

const CACHE_VERSION = 3 as const;
const CACHE_KEY_PREFIX = 'cp:workspace-eval-plan-level-settings:v3:';

type CachedWorkspaceEvaluationPlanLevelSettings = {
  version: typeof CACHE_VERSION;
  workspaceId: string;
  gradeId: string;
  settings: WorkspaceEvaluationPlanLevelSettings;
  savedAt: string;
};

function cacheKey(workspaceId: string, gradeId?: string): string {
  const ws = workspaceId.trim();
  const gid = (gradeId ?? '').trim();
  return gid ? `${CACHE_KEY_PREFIX}${ws}:${gid}` : `${CACHE_KEY_PREFIX}${ws}`;
}

export function loadWorkspaceEvaluationPlanLevelSettingsCache(
  workspaceId: string,
  gradeId?: string
): { found: false } | { found: true; settings: WorkspaceEvaluationPlanLevelSettings } {
  const ws = workspaceId.trim();
  const gid = (gradeId ?? '').trim();
  if (!ws) return { found: false };
  try {
    const raw = localStorage.getItem(cacheKey(ws, gid || undefined));
    if (!raw) return { found: false };
    const parsed = JSON.parse(raw) as CachedWorkspaceEvaluationPlanLevelSettings;
    if (parsed?.version !== CACHE_VERSION || parsed.workspaceId !== ws) return { found: false };
    if (gid && parsed.gradeId !== gid) return { found: false };
    const empty = emptyWorkspaceEvaluationPlanLevelSettings();
    return {
      found: true,
      settings: {
        defaultLevelStepCount: parsed.settings?.defaultLevelStepCount ?? empty.defaultLevelStepCount,
        labelsByStep: parsed.settings?.labelsByStep ?? empty.labelsByStep,
      },
    };
  } catch {
    return { found: false };
  }
}

export function saveWorkspaceEvaluationPlanLevelSettingsCache(
  workspaceId: string,
  settings: WorkspaceEvaluationPlanLevelSettings,
  gradeId?: string
): void {
  const ws = workspaceId.trim();
  const gid = (gradeId ?? '').trim();
  if (!ws) return;
  const empty = emptyWorkspaceEvaluationPlanLevelSettings();
  const payload: CachedWorkspaceEvaluationPlanLevelSettings = {
    version: CACHE_VERSION,
    workspaceId: ws,
    gradeId: gid,
    settings: {
      defaultLevelStepCount: settings.defaultLevelStepCount ?? empty.defaultLevelStepCount,
      labelsByStep: settings.labelsByStep ?? empty.labelsByStep,
    },
    savedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(cacheKey(ws, gid || undefined), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

export function clearWorkspaceEvaluationPlanLevelSettingsCache(workspaceId: string, gradeId?: string): void {
  const ws = workspaceId.trim();
  if (!ws) return;
  try {
    localStorage.removeItem(cacheKey(ws, (gradeId ?? '').trim() || undefined));
  } catch {
    /* ignore */
  }
}

export function defaultWorkspaceEvaluationPlanLevelSettings(): WorkspaceEvaluationPlanLevelSettings {
  return emptyWorkspaceEvaluationPlanLevelSettings();
}
